import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Auth } from 'aws-amplify';
import { apiEndpoint } from '../config';
import type { operations } from '../schema/directory-service';

type SuccessResponses<O extends keyof operations> = {
  [Status in Extract<
    keyof operations[O]['responses'],
    200 | 201 | 202 | 203 | 204
  >]: operations[O]['responses'][Status] extends { content: { 'application/json': infer B } } ? B : never;
};
type ResponseType = { [O in keyof operations]: SuccessResponses<O>[keyof SuccessResponses<O>] };
type ParamsType = {
  [P in keyof operations]: operations[P]['parameters'] extends Record<string, never>
    ? void
    : operations[P]['parameters'];
};

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: apiEndpoint,
    prepareHeaders: async (headers) => {
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();
      headers.set('authorization', token);
      return headers;
    },
  }),
  endpoints: (build) => ({
    commandUploadFile: build.mutation<
      ResponseType['UploadFile'],
      { jobId: ParamsType['UploadFile']['path']['jobId']; file: File; fileName: string }
    >({
      query: ({ file, fileName, jobId }) => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        return {
          url: `/jobs/${jobId}/commands/file-upload`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
    }),
    disableAccount: build.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/account/${id}`, method: 'DELETE' }),
    }),
    enableAccount: build.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/account/${id}/enable`, method: 'POST' }),
    }),
  }),
  reducerPath: 'directoryService',
  keepUnusedDataFor: process.env.NODE_ENV === 'test' ? 0 : undefined,
});

export const { useCommandUploadFileMutation, useDisableAccountMutation, useEnableAccountMutation } = api;
